import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField } from '@angular/material/form-field';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterOutlet } from '@angular/router';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { TranslocoPipe } from '@jsverse/transloco';
import { ApiService } from 'app/api.service';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/modules/admin/users/user.types';
import { HelpDeskComponent } from 'app/modules/modals/help-desk/help-desk.component';
import { Observable, Subject, map, startWith, takeUntil } from 'rxjs';
import { FuseFullscreenComponent } from '../../../../../@fuse/components/fullscreen/fullscreen.component';
import { FuseLoadingBarComponent } from '../../../../../@fuse/components/loading-bar/loading-bar.component';
import { FuseVerticalNavigationComponent as FuseVerticalNavigationComponent_1 } from '../../../../../@fuse/components/navigation/vertical/vertical.component';
import { LanguagesComponent } from '../../../common/languages/languages.component';
import { MessagesComponent } from '../../../common/messages/messages.component';
import { NotificationsComponent } from '../../../common/notifications/notifications.component';
import { SearchComponent } from '../../../common/search/search.component';
import { ShortcutsComponent } from '../../../common/shortcuts/shortcuts.component';
import { UserComponent } from '../../../common/user/user.component';

@Component({
    selector: 'classy-layout',
    templateUrl: './classy.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        CommonModule,
        FuseLoadingBarComponent,
        FuseVerticalNavigationComponent_1,
        NotificationsComponent,
        UserComponent,
        MatIcon,
        MatIconButton,
        MatFormField,
        MatInput,
        MatAutocompleteTrigger,
        FormsModule,
        ReactiveFormsModule,
        MatAutocomplete,
        MatOption,
        LanguagesComponent,
        FuseFullscreenComponent,
        SearchComponent,
        ShortcutsComponent,
        MessagesComponent,
        RouterOutlet,
        TranslocoPipe,
        MatIconModule,
        MatButtonModule,
        MatTooltipModule,
    ],
})
export class ClassyLayoutComponent implements OnInit, OnDestroy {
    isScreenSmall: boolean;
    navigation: Navigation;
    user: User;
    searchCommunitiesCtrl = new FormControl();
    searchCompaniesCtrl = new FormControl();
    filteredCommunity: Observable<any[]>;
    filteredCompany: Observable<any[]>;
    isLoading = false;
    community = new FormControl('');
    communities: any[] = []; // Lista en memoria
    company = new FormControl('');
    companies: any[] = []; // Lista en memoria
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    adminVersion: string;

    /**
     * Constructor
     */
    constructor(
        private _navigationService: NavigationService,
        private _userService: UserService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _api: ApiService,
        public dialog: MatDialog
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to navigation data
        this._navigationService.navigation$.pipe(takeUntil(this._unsubscribeAll)).subscribe((navigation: Navigation) => {
            this.navigation = navigation;
        });

        this._navigationService.version$.pipe(takeUntil(this._unsubscribeAll)).subscribe((version: string) => {
            this.adminVersion = version;
        });

        // Subscribe to the user service
        this._userService.user$.pipe(takeUntil(this._unsubscribeAll)).subscribe((user: User) => {
            this.user = user;
            this.searchCommunitiesCtrl.setValue(this.user['community']);
            this.searchCompaniesCtrl.setValue(this.user['company']);
        });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$.pipe(takeUntil(this._unsubscribeAll)).subscribe(({ matchingAliases }) => {
            this.isScreenSmall = !matchingAliases.includes('md');
        });

        // Cargar comunidades en memoria y asegurar que sea un array
        this._api.getComboboxAsgard('communities', {}).subscribe((response) => {
            this.communities = Array.isArray(response.data) ? response.data : [];
            this.filteredCommunity = this.searchCommunitiesCtrl.valueChanges.pipe(
                startWith(''),
                map((value) => this._filter(value, this.communities))
            );
        });

        // Cargar todas las empresas en memoria y validar array
        this._api.getComboboxAsgard('companies', {}).subscribe((response) => {
            this.companies = Array.isArray(response.data) ? response.data : [];
            this.filteredCompany = this.searchCompaniesCtrl.valueChanges.pipe(
                startWith(''),
                map((value) => this._filter(value, this.companies))
            );
        });
    }
    /**
     * Filtra en memoria la lista de comunidades o empresas
     * Ignora mayúsculas y minúsculas.
     */
    private _filter(value: any, collection: any[]): any[] {
        if (!Array.isArray(collection)) {
            return [];
        }

        // Asegurar que value es una cadena
        const filterValue = this.normalizeText(typeof value === 'string' ? value : '');

        return collection.filter((item) => this.normalizeText(item?.name || '').includes(filterValue));
    }

    /**
     * Normaliza texto para ignorar mayúsculas, minúsculas y tildes
     */
    private normalizeText(text: string): string {
        return text
            ? text
                  .toLowerCase()
                  .normalize('NFD')
                  .replace(/[\u0300-\u036f]/g, '')
            : '';
    }

    /**
     * Limpia el input cuando se hace focus
     */
    clearSearchInput(type: 'community' | 'company'): void {
        if (type === 'community') {
            this.searchCommunitiesCtrl.setValue('');
        } else if (type === 'company') {
            this.searchCompaniesCtrl.setValue('');
        }
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    getDisplayFn() {
        return (val) => (val && val.name ? val.name : val);
    }

    setCommunity(val) {
        this._api.setCommunityUser(val).subscribe(() => location.assign(''));
    }

    setCompany(val) {
        this._api.setCompanyUser(val).subscribe(() => location.assign(''));
    }

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);
        if (navigation) {
            navigation.toggle();
        }
    }

    /**
     * Open help-desk modal
     */
    openCreateTicketModal(): void {
        this.dialog.open(HelpDeskComponent, {
            panelClass: ['md:w-3/5', 'w-full'],
            maxHeight: '85vh',
            data: {},
        });
    }
}
